import React from 'react';
import { ShieldOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AI_OBJECTION_COPY } from '@/lib/ai/objectionNotice';

interface Props {
  variant?: 'banner' | 'inline';
  className?: string;
  /** Optional override of the description copy. */
  description?: string;
}

/**
 * Reusable notice surfaced anywhere an AI action is unavailable because the
 * current user has an active GDPR Art. 21 objection on file.
 *
 * - `banner`: full-width card for upload forms / paper detail pages.
 * - `inline`: compact one-liner for tight spaces (timeline rows, tooltips).
 */
export const AIObjectionNotice: React.FC<Props> = ({
  variant = 'banner',
  className,
  description,
}) => {
  const copy = description ?? AI_OBJECTION_COPY.description;

  if (variant === 'inline') {
    return (
      <p
        className={cn(
          'text-xs text-muted-foreground flex items-center gap-1.5',
          className,
        )}
      >
        <ShieldOff className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span>{copy}</span>
        <Link
          to={AI_OBJECTION_COPY.settingsHref}
          className="text-primary hover:underline whitespace-nowrap"
        >
          {AI_OBJECTION_COPY.actionLabel}
        </Link>
      </p>
    );
  }

  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20',
        className,
      )}
    >
      <ShieldOff
        className="h-4 w-4 mt-0.5 shrink-0 text-amber-700 dark:text-amber-300"
        aria-hidden="true"
      />
      <div className="space-y-1">
        <p className="font-medium text-amber-900 dark:text-amber-100">
          {AI_OBJECTION_COPY.title}
        </p>
        <p className="text-amber-800 dark:text-amber-200">{copy}</p>
        <Link
          to={AI_OBJECTION_COPY.settingsHref}
          className="inline-block text-xs font-medium text-primary hover:underline"
        >
          {AI_OBJECTION_COPY.actionLabel} →
        </Link>
      </div>
    </div>
  );
};

export default AIObjectionNotice;
